import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import serviceItemsService from "../../services/serviceItemsService";
import { getServiceItemTemplates } from "../../services/servicesApiV2";
import TechPicker from "../common/TechPicker";

/**
 * StepAssignmentModal
 *
 * Per-step technician assignment for a single service. Opened from the
 * "Split between techs…" entry in the service-row TechPicker (or by clicking
 * the multi-tech caption under the picker).
 *
 * Loads `request_service_tasks` for the service, lists each with a
 * TechPicker. Per-row changes PATCH live; bulk header uses the overwrite
 * cascade so it both replaces every task and updates the service-level lead.
 *
 * Bubbles latest `tech_assignment` up via `onTechAssignmentChanged` so the
 * service-row caption refreshes without a full reload.
 */
const StepAssignmentModal = ({
  show,
  onHide,
  service,
  technicians = [],
  onTechAssignmentChanged,
}) => {
  const requestServiceId = service?.id || service?.request_service_id;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingTaskId, setSavingTaskId] = useState(null);
  const [pendingBulkTechId, setPendingBulkTechId] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (show && requestServiceId) {
      loadTasks(cancelled);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, requestServiceId]);

  const loadTasks = async (cancelled) => {
    setLoading(true);
    setError(null);
    try {
      let items = await serviceItemsService.getItems(requestServiceId);

      // No per-job tasks yet — materialize templates from the master service
      // so the user sees the steps Preferred Products would show. Mirrors
      // ServiceItemsModal's template fallback, but creates them server-side
      // here since this modal PATCHes live (no Save button).
      if ((!items || items.length === 0)) {
        const masterServiceId =
          service?.master_service_id ||
          service?.service_id ||
          service?.installer_service_id;
        if (masterServiceId) {
          const templates = await getServiceItemTemplates(masterServiceId).catch(() => []);
          if (cancelled) return;
          if (templates && templates.length) {
            await Promise.all(templates.map(t =>
              serviceItemsService.createItem(requestServiceId, {
                label: t.label,
                type: t.type || 'material',
                recommended_product_id: t.recommended_product_id || null,
                recommended_product_name: t.recommended_product_name || null,
                recommended_product_brand: t.recommended_product_brand || null,
              })
            ));
            items = await serviceItemsService.getItems(requestServiceId);
          }
        }
      }

      if (cancelled) return;
      setTasks((items || []).map(t => ({
        id: t.id,
        label: t.label || '',
        type: t.type || 'material',
        assignedTechnicianId: t.assigned_technician_id ?? null,
      })));
    } catch (err) {
      console.error('[StepAssignmentModal] load failed:', err);
      if (!cancelled) setError('Failed to load steps.');
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  const handleRowChange = async (taskId, technicianId) => {
    // Optimistic update
    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, assignedTechnicianId: technicianId } : t
    ));
    setSavingTaskId(taskId);
    setError(null);
    try {
      const resp = await serviceItemsService.updateItem(taskId, {
        assigned_technician_id: technicianId,
      });
      const parent = resp?.parent_tech_assignment;
      if (parent && onTechAssignmentChanged) {
        onTechAssignmentChanged(parent);
      }
    } catch (err) {
      console.error('[StepAssignmentModal] PATCH failed:', err);
      setError('Failed to update assignment. Reload to see current state.');
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleBulkAssign = async () => {
    if (pendingBulkTechId === null || !requestServiceId) return;
    setBulkAssigning(true);
    setError(null);
    try {
      const resp = await serviceItemsService.bulkAssign(
        requestServiceId,
        pendingBulkTechId,
        { mode: 'overwrite' }
      );
      setTasks(prev => prev.map(t => ({ ...t, assignedTechnicianId: pendingBulkTechId })));
      const parent = resp?.tech_assignment;
      if (parent && onTechAssignmentChanged) {
        onTechAssignmentChanged(parent);
      }
      setPendingBulkTechId(null);
    } catch (err) {
      console.error('[StepAssignmentModal] bulk-assign failed:', err);
      setError('Failed to bulk assign.');
    } finally {
      setBulkAssigning(false);
    }
  };

  const serviceLabel = service
    ? `${service.service_category ? `${service.service_category} - ` : ''}${service.description || service.custom_description || service.service_name || ''}`
    : '';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-sharp fa-regular fa-users mr-2"></i>
          Assign technicians by step
          {serviceLabel && (
            <small className="text-muted d-block" style={{ fontSize: '0.65em' }}>
              {serviceLabel}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading steps…
          </div>
        ) : (
          <>
            {error && <div className="alert alert-danger">{error}</div>}

            {tasks.length === 0 ? (
              <div className="text-center text-muted py-3">
                No steps defined for this service. Add steps via Preferred Products (cube
                button on the service row), then come back here to assign technicians.
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="d-flex align-items-center">
                    <small className="text-muted mr-2">Assign all steps to:</small>
                    <TechPicker
                      technicians={technicians}
                      value={null}
                      onChange={(techId) => setPendingBulkTechId(techId)}
                      placeholder="Pick a tech…"
                      stopPropagation={false}
                      ariaLabel="Bulk assign all steps"
                    />
                  </div>
                  {pendingBulkTechId !== null && (
                    <div className="alert alert-warning d-flex align-items-center mt-2 py-2 px-3" role="alert">
                      <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2"></i>
                      <span className="flex-grow-1">
                        Reassign every step on this service? This overwrites existing per-step assignments
                        and updates the lead tech.
                      </span>
                      <Button
                        variant="warning"
                        size="sm"
                        className="ml-2"
                        onClick={handleBulkAssign}
                        disabled={bulkAssigning}
                      >
                        {bulkAssigning ? 'Assigning…' : 'Reassign all'}
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setPendingBulkTechId(null)}
                        disabled={bulkAssigning}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: '60%' }}>Step</th>
                        <th style={{ width: '40%' }}>Assignee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map(task => (
                        <tr key={task.id}>
                          <td className="align-middle">
                            <span>{task.label || '(unlabeled)'}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <TechPicker
                                technicians={technicians}
                                value={task.assignedTechnicianId}
                                onChange={(techId) => handleRowChange(task.id, techId)}
                                stopPropagation={false}
                                ariaLabel={`Assign ${task.label || 'step'}`}
                                disabled={savingTaskId === task.id}
                              />
                              {savingTaskId === task.id && (
                                <Spinner animation="border" size="sm" className="ml-2" />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StepAssignmentModal;
